/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_UPDATES.md
 *
 * Admin › SG Releases: every Scout Gateway release and where it is offered
 * (test → early → stable, stable in waves), the fleet by version, and the
 * controls the SG repository's "Promote SG release" workflow also has:
 * promote, set a wave, pause, resume, withdraw, put a boat on a channel.
 * A rollback reported by any SG pauses that release by itself.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type GatewayRelease } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { formatRelativeTime } from '../../utils/formatters';

const NEXT: Record<string, GatewayRelease['channel'] | null> = { test: 'early', early: 'stable', stable: null, withdrawn: null };
const CHANNEL_COLOUR: Record<string, string> = {
  test: 'bg-purple-500/15 text-purple-300', early: 'bg-cyan-500/15 text-cyan-300',
  stable: 'bg-green-500/15 text-green-300', withdrawn: 'bg-slate-600/40 text-slate-400',
};

function toast(ok: boolean, title: string, message: string) {
  useToastStore.getState().addToast({ type: ok ? 'success' : 'error', title, message });
}

export function AdminSgReleasesPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin-sg-releases'], queryFn: () => api.adminGetGatewayReleases() });
  const change = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.adminUpdateGatewayRelease>[1] }) => api.adminUpdateGatewayRelease(id, body),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['admin-sg-releases'] }); toast(true, 'Release changed', `${r.id}: ${r.channel}${r.paused ? ', paused' : ''}`); },
    onError: () => toast(false, 'Not changed', 'The release could not be changed. Try again.'),
  });
  const [serial, setSerial] = useState('');
  const [channel, setChannel] = useState<'test' | 'early' | 'stable'>('test');
  const setBoat = useMutation({
    mutationFn: () => api.adminSetGatewayChannel(serial.trim().toUpperCase(), channel),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['admin-sg-releases'] }); toast(true, 'Channel set', `${r.serial} is on ${r.channel}`); setSerial(''); },
    onError: () => toast(false, 'Not set', 'No Scout Gateway with that serial.'),
  });
  const [wave, setWave] = useState<Record<string, number>>({});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">SG Releases</h1>
        <p className="text-slate-400 mt-1">
          Scout Gateway software. A new release lands on test; promote it to early, then stable in waves.
          Each SG installs it by itself at a safe moment and goes back by itself if it fails; any rollback
          pauses the release for the whole fleet.
        </p>
      </div>

      {isLoading && <p className="text-slate-400">Loading…</p>}
      {isError && <p className="text-red-400">The releases could not be loaded.</p>}

      {data && (
        <>
          <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
            <h2 className="text-lg font-semibold text-white mb-3">The fleet</h2>
            {data.versions.length === 0 ? (
              <p className="text-slate-400">No Scout Gateways yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400"><th className="py-1">Version</th><th>Channel</th><th className="text-right">SGs</th></tr></thead>
                <tbody>
                  {data.versions.map((v) => (
                    <tr key={`${v.version}-${v.channel}`} className="border-t border-slate-700/60">
                      <td className="py-1.5 text-white">{v.version ? `SG ${v.version}` : 'Not reported'}</td>
                      <td className="text-slate-300">{v.channel}</td>
                      <td className="text-right text-white">{v.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
            <h2 className="text-lg font-semibold text-white mb-3">Releases</h2>
            {data.releases.length === 0 && <p className="text-slate-400">No releases yet. A tag sg-vX.Y.Z publishes one.</p>}
            <div className="space-y-3">
              {data.releases.map((r) => {
                const next = NEXT[r.channel];
                const pct = wave[r.id] ?? (r.channel === 'stable' ? r.rollout_percent : 10);
                return (
                  <div key={r.id} className="rounded-lg border border-slate-700 p-4" data-testid={`release-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-white font-semibold">SG {r.version}</span>
                      <span className="text-slate-400 font-mono text-sm">{r.id}</span>
                      <span className={`px-2 py-0.5 rounded text-sm ${CHANNEL_COLOUR[r.channel]}`}>
                        {r.channel}{r.channel === 'stable' && r.rollout_percent < 100 ? ` · ${r.rollout_percent}%` : ''}
                      </span>
                      {r.paused && <span className="px-2 py-0.5 rounded text-sm bg-amber-500/15 text-amber-300">Paused</span>}
                      <span className="text-slate-500 text-sm ml-auto">{formatRelativeTime(r.created_at)} · {(r.size / 1048576).toFixed(1)} MB</span>
                    </div>
                    {r.paused && r.paused_reason && <p className="text-amber-300 text-sm mt-2">{r.paused_reason}</p>}
                    {r.notes && <p className="text-slate-300 text-sm mt-2">{r.notes}</p>}
                    {r.channel !== 'withdrawn' && (
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {next && next !== 'stable' && (
                          <button className="px-3 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-sm"
                            onClick={() => change.mutate({ id: r.id, body: { channel: next } })}>Promote to {next}</button>
                        )}
                        {(next === 'stable' || r.channel === 'stable') && (
                          <>
                            <label className="text-slate-300 text-sm" htmlFor={`wave-${r.id}`}>Stable boats</label>
                            <input id={`wave-${r.id}`} type="number" min={1} max={100} value={pct}
                              onChange={(e) => setWave({ ...wave, [r.id]: Number(e.target.value) })}
                              className="w-20 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-sm" />
                            <span className="text-slate-400 text-sm">%</span>
                            <button className="px-3 py-1.5 rounded bg-green-600 hover:bg-green-500 text-white text-sm"
                              onClick={() => change.mutate({ id: r.id, body: { channel: 'stable', rollout_percent: Math.min(100, Math.max(1, pct)) } })}>
                              {r.channel === 'stable' ? 'Set wave' : 'Promote to stable'}
                            </button>
                          </>
                        )}
                        <button className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm"
                          onClick={() => change.mutate({ id: r.id, body: { paused: !r.paused } })}>{r.paused ? 'Resume' : 'Pause'}</button>
                        <button className="px-3 py-1.5 rounded bg-slate-700 hover:bg-red-700 text-white text-sm"
                          onClick={() => { if (window.confirm(`Withdraw SG ${r.version}? No boat will be offered it again.`)) change.mutate({ id: r.id, body: { channel: 'withdrawn' } }); }}>
                          Withdraw
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
            <h2 className="text-lg font-semibold text-white mb-1">Put a boat on a channel</h2>
            <p className="text-slate-400 text-sm mb-3">Boats are on stable unless set here. Test gets every release first; early a little later.</p>
            <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (/^SG-[0-9A-F]{16}$/i.test(serial.trim())) setBoat.mutate(); }}>
              <input aria-label="SG serial" placeholder="SG-XXXXXXXXXXXXXXXX" value={serial} onChange={(e) => setSerial(e.target.value)}
                className="w-64 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white font-mono text-sm" />
              <select aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}
                className="bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm">
                <option value="test">test</option><option value="early">early</option><option value="stable">stable</option>
              </select>
              <button type="submit" disabled={!/^SG-[0-9A-F]{16}$/i.test(serial.trim())}
                className="px-3 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm">Set</button>
            </form>
          </section>
        </>
      )}
    </div>
  );
}

export default AdminSgReleasesPage;
